import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { SonyAdminArticlePatchInputDto } from '@/Config/api/queryClient';
import { QueryKey } from '@/Config/api/queryKey';
import { Button } from '@/Domain/components/Shared/Button/Button';
import { Input } from '@/Domain/components/Shared/Input/Input';
import { Label } from '@/Domain/components/Shared/Label/Label';
import { AutosizeTextarea } from '@/Domain/components/Shared/Textarea/Textarea';
import { SonyAdminArticleDto } from '@/UI/KnowledgeBase/SonyDocuments/SonyDocuments.dto';

const articleSchema = z.object({
  title: z.string(),
  content: z.string(),
});

type ArticleFormValues = z.infer<typeof articleSchema>;

interface SonyArticleEditorProps {
  externalId: string;
  article: SonyAdminArticleDto | null;
  disabled: boolean;
}

export const SonyArticleEditor = ({ externalId, article, disabled }: SonyArticleEditorProps) => {
  const { t } = useTranslation('sony_admin');
  const patchArticleMutation = useMutation<unknown, unknown, SonyAdminArticlePatchInputDto>({
    mutationKey: [QueryKey.sonyAdmin.articlePatch],
  });

  const initialValues = useMemo<ArticleFormValues>(
    () => ({ title: article?.title ?? '', content: article?.content ?? '' }),
    [article],
  );
  const form = useForm<ArticleFormValues>({
    resolver: zodResolver(articleSchema),
    defaultValues: initialValues,
  });

  useEffect(() => {
    form.reset(initialValues, { keepDirtyValues: true });
  }, [initialValues, form]);

  const onSubmit = (values: ArticleFormValues): void => {
    const { dirtyFields } = form.formState;
    const payload: SonyAdminArticlePatchInputDto = { externalId };
    if (dirtyFields.title) payload.title = values.title;
    if (dirtyFields.content) payload.content = values.content;
    patchArticleMutation.mutate(payload, { onSuccess: () => form.reset(values) });
  };

  return (
    <section className="space-y-4">
      <h2 className="text-sm font-semibold text-foreground">{t('sheet.article_section')}</h2>
      {article === null ? (
        <p className="text-sm text-muted-foreground">{t('sheet.article_missing')}</p>
      ) : (
        <>
          <div className="space-y-1.5">
            <Label htmlFor="sony-article-title">{t('sheet.article_title')}</Label>
            <Controller
              control={form.control}
              name="title"
              render={({ field }) => (
                <Input
                  {...field}
                  id="sony-article-title"
                  size="sm"
                  disabled={disabled}
                  data-testid="sony-article-title-input"
                />
              )}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="sony-article-content">{t('sheet.article_content')}</Label>
            <Controller
              control={form.control}
              name="content"
              render={({ field }) => (
                <AutosizeTextarea
                  {...field}
                  id="sony-article-content"
                  minHeight={160}
                  maxHeight={480}
                  disabled={disabled}
                  data-testid="sony-article-content-input"
                  className="w-full rounded-xl border border-input bg-card px-3 py-2 text-sm text-foreground focus:outline-none disabled:cursor-not-allowed disabled:opacity-50"
                />
              )}
            />
          </div>
          <div className="flex justify-end">
            <Button
              size="sm"
              onClick={form.handleSubmit(onSubmit)}
              loading={patchArticleMutation.isPending}
              disabled={disabled || !form.formState.isDirty}
              data-testid="sony-save-article-button"
            >
              {t('sheet.save_article')}
            </Button>
          </div>
        </>
      )}
    </section>
  );
};
